import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { selectIsDarkMode } from '@/redux/theme';
import { RobotOutlined, CloseOutlined } from '@ant-design/icons';
import CompanyChatbot from '../CompanyChatbot';

const API_BASE = "ai-chat";

export default function ChatbotWidget() {
    const isDarkMode = useSelector(selectIsDarkMode);
    const [isOpen, setIsOpen] = useState(false);
    const [isEnabled, setIsEnabled] = useState(true);
    const [position, setPosition] = useState({ x: 30, y: 30 });
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
    const [hasMoved, setHasMoved] = useState(false);
    const widgetRef = useRef(null);

    // Check if chatbot is enabled
    useEffect(() => {
        axios.get(`${API_BASE}/status`)
            .then(res => {
                setIsEnabled(res.data.enabled !== false);
            })
            .catch(() => { });
    }, []);

    const toggleChat = () => {
        if (hasMoved) {
            setHasMoved(false);
            return;
        }
        setIsOpen(!isOpen);
    };

    const handleMouseDown = (e) => {
        setIsDragging(true);
        setDragStart({
            x: e.clientX + position.x,
            y: e.clientY + position.y
        });
        setHasMoved(false);
    };

    useEffect(() => {
        const handleMouseMove = (e) => {
            if (!isDragging) return;
            const newX = dragStart.x - e.clientX;
            const newY = dragStart.y - e.clientY;
            const maxX = window.innerWidth - 60;
            const maxY = window.innerHeight - 60;
            setPosition({
                x: Math.max(0, Math.min(newX, maxX)),
                y: Math.max(0, Math.min(newY, maxY))
            });
            if (Math.abs(newX - position.x) > 5 || Math.abs(newY - position.y) > 5) {
                setHasMoved(true);
            }
        };

        const handleMouseUp = () => setIsDragging(false);

        if (isDragging) {
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);
        }
        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDragging, dragStart, position]);

    // Don't render if chatbot is disabled by admin
    if (!isEnabled) return null;

    return (
        <div
            ref={widgetRef}
            style={{
                position: 'fixed',
                bottom: `${position.y}px`,
                right: `${position.x}px`,
                zIndex: 9999
            }}
        >
            {/* Chat Window */}
            {isOpen && (
                <div
                    className="chatbot-window"
                    style={{
                        position: 'absolute',
                        bottom: '80px',
                        right: '0',
                        width: 'min(400px, 90vw)',
                        height: 'min(620px, 85vh)',
                        maxHeight: 'calc(100vh - 120px)',
                        boxShadow: isDarkMode
                            ? '0 12px 28px rgba(0, 0, 0, 0.6)'
                            : '0 12px 28px rgba(0, 0, 0, 0.15)',
                        borderRadius: '12px',
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                        border: isDarkMode ? '1px solid #30363d' : '1px solid #d0d7de',
                        animation: 'slideUp 0.3s ease-out',
                        cursor: 'default',
                        backgroundColor: isDarkMode ? '#161b22' : '#ffffff'
                    }}
                >
                    <div style={{ position: 'relative', height: '100%', width: '100%' }}>
                        <button
                            onClick={() => setIsOpen(false)}
                            style={{
                                position: 'absolute', top: '10px', right: '10px',
                                background: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
                                border: 'none', borderRadius: '50%',
                                width: '30px', height: '30px',
                                color: isDarkMode ? '#fff' : '#666',
                                cursor: 'pointer', zIndex: 10,
                                display: 'flex', alignItems: 'center',
                                justifyContent: 'center', fontSize: '14px'
                            }}
                        ><CloseOutlined /></button>
                        <CompanyChatbot />
                    </div>
                </div>
            )}

            {/* Floating Button */}
            <button
                onMouseDown={handleMouseDown}
                onClick={toggleChat}
                style={{
                    width: '60px', height: '60px', borderRadius: '50%',
                    background: 'linear-gradient(135deg, #1890ff, #3ecfcf)',
                    border: 'none',
                    boxShadow: isDarkMode
                        ? '0 6px 16px rgba(0, 0, 0, 0.5)'
                        : '0 6px 16px rgba(24, 144, 255, 0.3)',
                    cursor: isDragging ? 'grabbing' : 'grab',
                    display: 'flex', alignItems: 'center',
                    justifyContent: 'center', fontSize: '28px',
                    color: '#fff',
                    transition: isDragging ? 'none' : 'transform 0.2s',
                    outline: 'none', touchAction: 'none'
                }}
                onMouseEnter={e => !isDragging && (e.currentTarget.style.transform = 'scale(1.1)')}
                onMouseLeave={e => !isDragging && (e.currentTarget.style.transform = 'scale(1)')}
            >
                {isOpen ? <CloseOutlined /> : <RobotOutlined />}
            </button>

            <style>{`
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @media (max-width: 480px) {
          .chatbot-window {
            right: -10px !important;
            width: 95vw !important;
            height: 70vh !important;
          }
        }
      `}</style>
        </div>
    );
}
